import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import IdentifiersSection from '../../components/identity/IdentifiersSection.jsx';
import LogoSection from '../../components/identity/LogoSection.jsx';
import Alert from '../../components/ui/Alert.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import DomainStatus from './DomainStatus.jsx';
import NameModal from './NameModal.jsx';
import SetupPrompt from '../../components/SetupPrompt.jsx';

/** Summary card: set it up, or — once the code is on their site — that they're protected. */
function WebsiteProtectionCard({ protection: p }) {
  const copies = p?.unreviewed_copies || 0;
  if (!p?.installed) {
    return (
      <Section
        title="Website protection"
        description="One line of code on your website proves it’s yours, warns you when someone copies it, and can show visitors an “Official site” badge."
        actions={<Link to="/company/website" className="btn btn-primary btn-small">Set it up</Link>}
      >
        {copies > 0 && <Alert tone="danger">Your code has run on {copies} site{copies === 1 ? '' : 's'} that {copies === 1 ? 'isn’t' : 'aren’t'} yours. <Link to="/company/website">Review</Link></Alert>}
      </Section>
    );
  }
  return (
    <Section
      title={<span className="title-with-pill">Website protection {copies > 0 ? <Pill tone="bad">{copies} possible cop{copies === 1 ? 'y' : 'ies'}</Pill> : <Pill tone="good">Protected</Pill>}</span>}
      description={copies > 0
        ? `Your code has run on ${copies} site${copies === 1 ? '' : 's'} that ${copies === 1 ? 'isn’t' : 'aren’t'} yours — usually a copy of your website. Review ${copies === 1 ? 'it' : 'them'} now.`
        : `Your website code is in place${p.last_seen ? `, last seen ${fmtDateTime(p.last_seen)}` : ''}. We’ll warn you if anyone copies your site.`}
      actions={<Link to="/company/website" className={`btn btn-small ${copies > 0 ? 'btn-danger' : 'btn-secondary'}`}>{copies > 0 ? 'Review' : 'View'}</Link>}
    />
  );
}

/** The customer's company: its identity (inherited by every event) and its brands. */
export default function Company() {
  const { organisation: current, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);   // 'rename' | 'brand'

  const load = useCallback(async () => {
    if (!current) return;
    try { setData(await api(`/portal/organisations/${current.id}`)); } catch (err) { setError(err.message); }
  }, [current]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="page"><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;

  const { organisation: org, brands, identifiers, domains } = data;
  const activeBrands = brands.filter((b) => b.status === 'active');
  const base = `/portal/organisations/${org.id}`;

  const rename = async (name) => {
    await api(base, { method: 'PATCH', body: { name } });
    setModal(null);
    await Promise.all([load(), refresh()]);
  };
  const addBrand = async (name) => {
    const { brand } = await api(`${base}/brands`, { method: 'POST', body: { name } });
    navigate(`/company/brands/${brand.id}`);
  };

  return (
    <div className="page">
      <PageHeader
        title={org.name}
        subtitle="Everything here belongs to the whole company, so every brand and event uses it."
        actions={org.can_manage && <button type="button" className="btn btn-secondary" onClick={() => setModal('rename')}>Rename</button>}
      />

      <SetupPrompt />

      <Section
        title="Domains"
        description="Proving you own your domains is what lets us tell people a message really is from you."
        flush={domains.length > 0}
      >
        {domains.length === 0 ? (
          <EmptyState>Add your email domain or website under “Company channels” below, then come back here to prove it’s yours.</EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Domain</th><th>Ownership</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {domains.map((d) => (
                  <tr key={d.id}>
                    <td data-label="Domain" className="mono">{d.domain}</td>
                    <td data-label="Ownership"><DomainStatus status={d.status} /></td>
                    <td className="right row-actions">
                      <Link to={`/company/domains/${d.id}`} className={`btn btn-small ${d.status === 'verified' ? 'btn-secondary' : 'btn-primary'}`}>
                        {d.status === 'verified' ? 'View' : 'Verify'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Team"
        description="The people who can sign in and work on this company. Invite colleagues so everyone has their own account — and a colleague with an address on your domain helps prove the domain is yours."
        actions={<Link to="/company/team" className={`btn btn-small ${org.can_manage ? 'btn-primary' : 'btn-secondary'}`}>{org.can_manage ? 'Manage team' : 'View team'}</Link>}
      />

      <WebsiteProtectionCard protection={data.website_protection} />
      <Section
        title="Your email services"
        description="The services you really send email through (your mailbox, newsletters, invoices). Email from a service you don’t use can’t be verified, even if it passes all the usual email checks."
        actions={<Link to="/company/email-services" className="btn btn-secondary btn-small">View</Link>}
      />

      <LogoSection base={base} entityId={org.entity_id} ownerName={org.name} canEdit={org.can_manage}
        description="Shown on your events’ public verify pages (unless an event or brand has its own). We also compare it with the images in emails people forward to us: an email using an old or altered version of your logo is a warning sign." />

      <IdentifiersSection
        onChange={() => load()}
        title="Company channels"
        description="A “channel” is anything people might get a message or payment request from: an email domain or address, a website, a phone number, a bank account. Listing the real ones lets anyone check a message against them. These count for every event."
        basePath={`${base}/entities/${org.entity_id}/identifiers`}
        ownerName={org.name}
        initial={identifiers}
        canEdit={org.can_manage}
      />

      <Section
        title="Brands"
        description="Names you run events under, if they have their own domains or websites. Skip this if all your events go out as the company."
        actions={org.can_manage && <button type="button" className="btn btn-primary btn-small" onClick={() => setModal('brand')}>Add brand</button>}
        flush={brands.length > 0}
      >
        {brands.length === 0 ? (
          <EmptyState>No brands. Your events belong to {org.name} directly.</EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Brand</th><th>Own channels</th><th>Events</th><th>Status</th></tr></thead>
              <tbody>
                {brands.map((b) => (
                  <tr key={b.id}>
                    <td data-label="Brand"><Link className="strong-link" to={`/company/brands/${b.id}`}>{b.name}</Link></td>
                    <td data-label="Own channels">{b.identifier_count}</td>
                    <td data-label="Events">{b.event_count}</td>
                    <td data-label="Status">{b.status === 'active' ? <Pill tone="good">Active</Pill> : <Pill>Archived</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Events" description={`${activeBrands.length ? 'Each event belongs to a brand or to the company, and' : 'Each event'} uses the channels above as well as its own.`}
        actions={<Link to="/events" className="btn btn-secondary btn-small">Go to events</Link>} />

      {modal === 'rename' && (
        <NameModal title="Rename the company" label="Company name" initial={org.name} submitLabel="Save"
          onClose={() => setModal(null)} onSave={rename} />
      )}
      {modal === 'brand' && (
        <NameModal title="Add a brand" label="Brand name" placeholder="TechSummit" submitLabel="Add brand"
          description="A name you run events under. You’ll add its own domains and website next."
          onClose={() => setModal(null)} onSave={addBrand} />
      )}
    </div>
  );
}
